// web/src/components/layout/UserMenu.jsx
// Top-right dropdown: user, barn switcher, view-as switcher, settings, sign out.
//
// The barn switcher only appears when you belong to more than one barn, which
// until now nobody did. It matters more than it looks: login only auto-selects
// a barn when you're a member of exactly ONE, so a second membership drops you
// on "No barn selected" with no way out. The /auth/switch-barn endpoint has
// existed since Session 1 and was never wired to anything.

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth, useLogout } from '../../lib/auth';

const ROLE_LABEL = {
  owner: 'Owner',
  manager: 'Manager',
  trainer: 'Trainer',
  groom: 'Groom',
  client: 'Client',
  service_provider: 'Service provider',
};

export default function UserMenu() {
  const { user, barns, viewAsRole, availableViewAsRoles, setViewAsRole } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const myBarns = barns ?? [];
  const showBarnSwitcher = myBarns.length > 1;

  const switchBarn = useMutation({
    mutationFn: (barnId) => api.post('/auth/switch-barn', { barnId }),
    onSuccess: () => {
      // Everything on screen is barn-scoped, so the safest thing after
      // switching is to drop the whole cache rather than guess which keys
      // survive. Then land on Home, since the current page may not exist for
      // the new barn (or the role there).
      qc.clear();
      setOpen(false);
      navigate('/dashboard', { replace: true });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) return null;

  const displayName = user.name || user.email || '';
  const initials = displayName.split(' ').map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
  const showSwitcher = availableViewAsRoles.length > 0;

  // Wait for the server to confirm logout (session deleted + cookie cleared)
  // before leaving — important on shared barn devices — then go to /login.
  // useLogout() also clears the query cache and the view-as flag on success.
  function handleLogout() {
    logout.mutate(undefined, {
      onSuccess: () => {
        setOpen(false);
        navigate('/login', { replace: true });
      },
    });
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 rounded-full bg-hunter text-white text-sm font-medium flex items-center justify-center hover:bg-hunter-light transition"
        aria-label="User menu"
        aria-expanded={open}
      >
        {initials}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-ink-100 rounded-lg shadow-lg z-50 py-1">
          <div className="px-3 py-2 border-b border-ink-100">
            <div className="font-medium truncate">{user.name}</div>
            <div className="text-xs text-ink-400 truncate">{user.email}</div>
            {user.role && (
              <div className="text-xs text-ink-400 mt-1">
                {ROLE_LABEL[user.role] ?? user.role}
              </div>
            )}
          </div>

          {showBarnSwitcher && (
            <>
              <div className="px-3 pt-2 pb-1 text-xs font-medium text-ink-400 uppercase tracking-wide">
                Barn
              </div>
              {myBarns.map((b) => (
                <MenuItem
                  key={b.id}
                  onClick={() => {
                    if (b.id !== user.activeBarnId) switchBarn.mutate(b.id);
                    else setOpen(false);
                  }}
                  active={b.id === user.activeBarnId}
                >
                  <span className="truncate">{b.name}</span>
                  <span className="text-ink-300"> · {ROLE_LABEL[b.role] ?? b.role}</span>
                </MenuItem>
              ))}
              {switchBarn.isError && (
                <p className="px-3 pb-1 text-xs text-red-600">Couldn’t switch barn.</p>
              )}
              <div className="border-t border-ink-100 my-1" />
            </>
          )}

          {showSwitcher && (
            <>
              <div className="px-3 pt-2 pb-1 text-xs font-medium text-ink-400 uppercase tracking-wide">
                View as
              </div>
              <MenuItem
                onClick={() => { setViewAsRole(null); setOpen(false); }}
                active={!viewAsRole}
              >
                {ROLE_LABEL[user.role]} <span className="text-ink-300">(your role)</span>
              </MenuItem>
              {availableViewAsRoles.map((role) => (
                <MenuItem
                  key={role}
                  onClick={() => { setViewAsRole(role); setOpen(false); }}
                  active={viewAsRole === role}
                >
                  {ROLE_LABEL[role] ?? role}
                </MenuItem>
              ))}
              <div className="border-t border-ink-100 my-1" />
            </>
          )}

          <Link
            to="/horses"
            className="block px-3 py-2 text-sm hover:bg-ink-50"
            onClick={() => setOpen(false)}
          >
            Horses
          </Link>
          <button
            onClick={handleLogout}
            disabled={logout.isPending}
            className="w-full text-left px-3 py-2 text-sm hover:bg-ink-50 disabled:opacity-60"
          >
            {logout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
          {logout.isError && (
            <p className="px-3 pb-2 text-xs text-red-600">Couldn’t sign out. Try again.</p>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, active, children }) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-ink-50 ' +
        (active ? 'font-medium' : 'text-ink-600')
      }
    >
      <span>{children}</span>
      {active && <span className="text-emerald-600">✓</span>}
    </button>
  );
}
